import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Box, IconButton, styled, Typography } from '@mui/material';
import dayjs from 'dayjs';
import GenericDraggableTableView from '../../../../Features/Table/GenericDraggableTable';
import CustomIcon from '../../../../Components/CustomIcon';
import { useTranslation } from 'react-i18next';
import { getColumnsConfig } from '../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations';
import { MAX_COLUMN_SELECTION_COUNT } from '../../../../Helper/DigitalTwin/DataFile';
import { usePagesContent } from '../../../../Hooks/usePagesContent';
import { groupEventDetailsData } from '../../../DigitalTwinV3/Tabs/BatteryPerformance/CommonFunctions';
import { fetchFiltersDataObject } from '../../../../Helper/Table/TableHelper';
import { EVENT_GROUP_COLUMN, useEventDetailsTableConfig } from '../../../DigitalTwinV3/Tabs/BatteryPerformance/eventDetailsTableConfig';
import { fleetEnergyEventsAction, getFleetEnergyEvents } from '../../../../Api/PerformanceApi';
import ProgressBarColumnView from '../../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/ProgressBarColumnView';
import { getSOHColor } from '../../../../Helper/BatteryHealth/BatteryHealthHelper';

const groupByDateField = (flatData, field) => {
    const formatDateGroup = (dateString) => dayjs(dateString, "YYYY-MM-DD HH:mm:ss").format("D MMM YYYY");
    const groupedByDate = flatData.reduce((acc, event) => {
        const dateGroup = formatDateGroup(event[field]);
        if (!acc[dateGroup]) acc[dateGroup] = [];
        acc[dateGroup].push(event);
        return acc;
    }, {});
    const sortedDateGroups = Object.entries(groupedByDate).sort(([dateA], [dateB]) =>
        dayjs(dateB, "D MMM YYYY").valueOf() - dayjs(dateA, "D MMM YYYY").valueOf()
    );
    return sortedDateGroups.map(([dateGroup, events], groupIndex) => {
        const sortedEvents = [...events].sort((a, b) => dayjs(a[field]).valueOf() - dayjs(b[field]).valueOf());
        const children = sortedEvents.map((event, eventIndex) => ({
            ...event,
            event_group: dateGroup,
            key: `event_${groupIndex}_${eventIndex}`,
        }));
        return { event_group: dateGroup, key: `group_${groupIndex}`, children };
    });
};

const TableContainer = styled(Box)(() => ({
    width: '100%',
    minHeight: '200px',
    '& .ant-table-cell-with-append': { display: 'flex', alignItems: 'center' },
}));

const ExpandIconComponent = ({ expanded, onExpand, record }) => {
    if (!record.children || record.children.length === 0) return null;
    return (
        <IconButton
            disableRipple
            disableTouchRipple
            disableFocusRipple
            sx={{
                fontSize: '16px',
                paddingLeft: 0,
                backgroundColor: 'transparent !important',
                '&:hover': { backgroundColor: 'transparent !important' },
            }}
            onClick={e => onExpand(record, e)}
        >
            <CustomIcon name="primary-arrow-icon" rotate={expanded ? 90 : 0} />
        </IconButton>
    );
};

const customRenderers = {
    event_group: (record, config) => {
        const isDischarge = record?.eventType?.toLowerCase() === 'discharging';
        return (
            <Box sx={{ backgroundColor: isDischarge ? '#FFF2F2' : 'transparent', padding: '0 4px' }}>
                <Typography variant="fw400s14lh24c0061CA" sx={{ color: "#3D4B5A !important" }}>
                    {record[config.dataIndex]}
                </Typography>
            </Box>
        );
    },
    'progress-bar': (record, config) => {
        const value = record?.[config?.dataIndex];
        const decimalPlaces = config?.decimalPlaces;
        const barColor = getSOHColor(value);
        const startProgressFrom = config?.startProgressFrom || 0;
        const unitPostFix = config?.unitPostfix || "";
        const defaultValue = config?.defaultValue || "Not Available";
        return (
            <ProgressBarColumnView
                minProgress={startProgressFrom}
                defaultText={defaultValue}
                value={value ? value?.toFixed(decimalPlaces) : 0}
                barColor={barColor}
                record={record}
                config={config}
                unitPostFix={unitPostFix}
            />
        );
    },
};

/**
 * Custom platform Event Details table.
 *
 * Accepts optional allowedEventTypes (e.g. ["charging", "discharging"]) to
 * filter which event types appear in the table. When omitted all event types
 * are shown.
 */
const CustomEventDetailsTable = ({
    batteryID,
    startDate,
    endDate,
    allowedEventTypes,
}) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const { t } = useTranslation(['common', 'generic_table']);
    const pageContent = usePagesContent();
    const [allTableColumns, setAllTableColumns] = useState([]);
    const [currentTableColumns, setCurrentTableColumns] = useState([]);
    const [selectedTableColumns, setSelectedTableColumns] = useState([]);
    const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([]);
    const [columnOrder, setColumnOrder] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [filters, setFilters] = useState({});
    const [selectedFilters, setSelectedFilters] = useState({});
    const [filtersChanged, setFiltersChanged] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [eventsData, setEventsData] = useState([]);
    const prevFiltersRef = useRef(null);
    const prevDatesRef = useRef(null);
    const isInitialMount = useRef(true);

    const customEventDetailsTable = useMemo(
        () => pageContent?.metaInfo?.customEventDetailsTable ?? null,
        [pageContent?.metaInfo]
    );

    // Columns and filters from the org's custom platform config, falling back to
    // the standard Event Details data files - resolved by the same helper the
    // Digital Twin Performance tab uses.
    const eventDetailsConfig = useEventDetailsTableConfig(customEventDetailsTable);

    const getBaseListActionApiParams = useCallback(() => {
        const eventTypeColumn = allTableColumns.find((column) => column.dataIndex === 'eventType');
        const updatedColumnOrder = eventTypeColumn ? [eventTypeColumn.headerLabel, ...columnOrder] : columnOrder;
        const updatedColumns = eventTypeColumn ? [eventTypeColumn, ...currentTableColumns] : currentTableColumns;
        return {
            batteryID,
            startDate,
            endDate,
            columnOrder: updatedColumnOrder,
            columns: JSON.stringify(updatedColumns),
            ...selectedFilters,
            ...(allowedEventTypes?.length && { eventType: allowedEventTypes }),
        };
    }, [batteryID, startDate, endDate, columnOrder, currentTableColumns, selectedFilters, allTableColumns, allowedEventTypes]);

    const fetchEventsData = useCallback(async () => {
        try {
            setIsLoading(true);
            const fleetEventsResponse = await getFleetEnergyEvents({
                batteryID: [batteryID],
                startDate,
                endDate,
                skipPagination: true,
                ...selectedFilters,
                ...(allowedEventTypes?.length && { eventType: allowedEventTypes }),
            });
            const raw = fleetEventsResponse?.response?.data ?? [];
            setEventsData(raw);
        } catch (error) {
            console.error('CustomEventDetailsTable: error fetching events:', error);
            setEventsData([]);
        } finally {
            setIsLoading(false);
        }
    }, [batteryID, startDate, endDate, selectedFilters, filtersChanged, allowedEventTypes]);

    const loadColumnData = useCallback(() => {
        const columnsConfig = getColumnsConfig({
            tableColumns: eventDetailsConfig.columnsConfig,
            tableColumnOrder: eventDetailsConfig.columnOrdering,
            customRenderers,
            t,
        });
        let uniqueColumnsConfig = columnsConfig.filter((item, index, self) =>
            index === self.findIndex((t) => t.dataIndex === item.dataIndex)
        );
        const defaultColumns = uniqueColumnsConfig.sort((a, b) => {
            if (a.fixed && !b.fixed) return -1;
            if (!a.fixed && b.fixed) return 1;
            return 0;
        }).slice(0, MAX_COLUMN_SELECTION_COUNT);

        setAllTableColumns(uniqueColumnsConfig);
        const filteredColumns = defaultColumns.filter((column) => column.dataIndex !== 'eventType');
        setCurrentTableColumns(filteredColumns);
        setSelectedTableColumns(filteredColumns.map(column => column.dataIndex));
        setDefaultSelectedColumns(filteredColumns.map(column => column.dataIndex));
        setColumnOrder(filteredColumns.map((column) => column.headerLabel));
    }, [t, eventDetailsConfig]);

    const onColumnOrderChange = (tableColumns) => {
        setColumnOrder(tableColumns.map((value) => value.headerLabel));
    };

    const applyFilters = (args) => {
        setSelectedFilters(args);
        setFiltersChanged(filtersChanged + 1);
    };

    useEffect(() => { loadColumnData(); }, [loadColumnData]);

    useEffect(() => {
        const initializeFilters = async () => {
            const eventDetailsFilters = eventDetailsConfig.filters;
            if (!eventDetailsFilters || eventDetailsFilters.length === 0) return;
            const filtersObj = await fetchFiltersDataObject(eventDetailsFilters, {});
            const sortedFiltersObj = Object.fromEntries(
                Object.entries(filtersObj).sort(([, a], [, b]) => a.label.localeCompare(b.label))
            );
            setFilters(sortedFiltersObj);
        };
        initializeFilters();
    }, [eventDetailsConfig.filters]);

    useEffect(() => {
        if (batteryID && startDate && endDate) {
            if (isInitialMount.current) {
                isInitialMount.current = false;
                prevFiltersRef.current = selectedFilters;
                prevDatesRef.current = { startDate, endDate };
                fetchEventsData();
                return;
            }
            const datesChanged = prevDatesRef.current?.startDate !== startDate || prevDatesRef.current?.endDate !== endDate;
            if (filtersChanged || datesChanged) {
                prevFiltersRef.current = selectedFilters;
                prevDatesRef.current = { startDate, endDate };
                fetchEventsData();
            }
        }
    }, [batteryID, startDate, endDate, selectedFilters, fetchEventsData, filtersChanged]);

    useEffect(() => {
        if (!isLoading && eventsData.length > 0) {
            const orgGroupBy = customEventDetailsTable?.groupBy;
            const groupedData = orgGroupBy
                ? groupByDateField(eventsData, orgGroupBy)
                : groupEventDetailsData(eventsData);
            setTableData(groupedData);
            if (groupedData.length > 0 && groupedData[0].children?.length > 0) {
                setExpandedRowKeys([groupedData[0].key]);
            }
        } else if (!isLoading && eventsData.length === 0) {
            setTableData([]);
            setExpandedRowKeys([]);
        }
    }, [eventsData, isLoading, pageContent?.metaInfo?.customEventDetailsTable?.groupBy]);

    const updateSelectedTableColumns = (newSelectedColumns) => {
        const filteredColumns = allTableColumns.filter((columnConfig) =>
            newSelectedColumns.includes(columnConfig.dataIndex)
        );
        setCurrentTableColumns(filteredColumns);
        setSelectedTableColumns(newSelectedColumns);
        setColumnOrder(filteredColumns.map((col) => col.headerLabel || col.title));
    };

    const getCSVFileName = () => {
        return `BatteryPerformance_${batteryID}_${dayjs().format("MMMM D, YYYY")}.csv`;
    };

    const getCsvColumnParams = () => {
        // The group column is a display aid, not data. Matched by key and by its
        // own header, so a renamed Group column is still left out.
        const groupHeader = allTableColumns.find(column => column.key === EVENT_GROUP_COLUMN.key)?.headerLabel ?? EVENT_GROUP_COLUMN.title;
        const columnsForDownload = currentTableColumns.filter(column => column.key !== EVENT_GROUP_COLUMN.key);
        const columnOrderForDownload = columnOrder.filter(column => column !== groupHeader);
        const eventTypeColumn = allTableColumns.find((column) => column.dataIndex === 'eventType');
        const updatedColumns = eventTypeColumn ? [eventTypeColumn, ...columnsForDownload] : columnsForDownload;
        const updatedColumnOrder = eventTypeColumn ? [eventTypeColumn.headerLabel, ...columnOrderForDownload] : columnOrderForDownload;
        return { columns: JSON.stringify(updatedColumns), columnOrder: updatedColumnOrder };
    };

    const getCSVData = async () => {
        const res = await fleetEnergyEventsAction({ actionType: "download-csv", ...getBaseListActionApiParams() });
        return res?.response?.data || [];
    };

    const onExpand = (expanded, record) => {
        const keys = [...expandedRowKeys];
        if (expanded) {
            keys.push(record.key);
        } else {
            const index = keys.indexOf(record.key);
            if (index > -1) keys.splice(index, 1);
        }
        setExpandedRowKeys(keys);
    };

    return (
        <TableContainer>
            <GenericDraggableTableView
                tableID={'EventDetailsTable'}
                title={t('digital_twin:event_details', 'Event Details')}
                containerStyles={{ width: 'auto' }}
                disableSearch={true}
                getCSVData={getCSVData}
                getCSVFileName={getCSVFileName}
                getCsvColumnSettings={getCsvColumnParams}
                showEmailComposer={false}
                alwaysEnableActions={true}
                showPrimaryButton={false}
                tableColumns={currentTableColumns}
                onColumnOrderChange={onColumnOrderChange}
                columnOrder={columnOrder}
                tableData={tableData}
                isLoading={isLoading}
                disablePagination={true}
                emptyMessage={t('common:no_data_found', 'No data found')}
                customRightActions={true}
                expandable={{ expandedRowKeys, onExpand, expandIcon: ExpandIconComponent }}
                initialColumns={allTableColumns}
                selectedColumns={selectedTableColumns}
                applySelectedColumns={updateSelectedTableColumns}
                filters={filters}
                selectedFilters={selectedFilters}
                applyFilters={(args) => applyFilters(args)}
                maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
                defaultSelectedColumns={defaultSelectedColumns}
            />
        </TableContainer>
    );
};

export default CustomEventDetailsTable;
